/**
 * Store selector: pickup locations from Storefront GraphQL (pickup-enabled locations only).
 */
(() => {
  "use strict";

  const escapeHtml = (s) => {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  /** Matches snippets/icon-store-locator.liquid (address row in location cards). */
  const STORE_LOCATOR_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" class="store-selector-icon" aria-hidden="true" focusable="false"><path d="M13.5 3.5H2.83337" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="square"/><path d="M2.16675 7.5V14.5C2.16675 15.0523 2.61446 15.5 3.16675 15.5H7.16675C7.71903 15.5 8.16675 15.0523 8.16675 14.5V12.5C8.16675 11.9477 8.61446 11.5 9.16675 11.5H10.5001C11.0524 11.5 11.5001 11.9477 11.5001 12.5V14.5C11.5001 15.0523 11.9478 15.5 12.5001 15.5H13.1667C13.719 15.5 14.1667 15.0523 14.1667 14.5V7.5" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="square"/><path d="M13.5 3.16667C13.5 3.38304 13.5702 3.59357 13.7 3.76667L15.3 5.9C15.4299 6.0731 15.5 6.28363 15.5 6.5C15.5 7.05229 15.0523 7.5 14.5 7.5H1.83337C1.28109 7.5 0.833374 7.05229 0.833374 6.5C0.833374 6.28363 0.903552 6.0731 1.03337 5.9L2.63337 3.76667C2.7632 3.59357 2.83337 3.38304 2.83337 3.16667V1.83334C2.83337 1.28105 3.28109 0.833336 3.83337 0.833336H12.5C13.0523 0.833336 13.5 1.28105 13.5 1.83334V3.16667Z" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="square"/><path d="M6.16675 9.5H4.16675V11.5H6.16675V9.5Z" fill="currentColor"/></svg>';

  const haversineMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  /**
   * Shopify rich_text_field JSON uses bold/italic (and sometimes underline) as booleans on `text`
   * nodes — not only separate bold/italic wrapper nodes. See shopify.dev metafield rich text format.
   */
  const richTextTextNodeToHtml = (node) => {
    if (!node || node.type !== "text") return "";
    let t = escapeHtml(node.value || "");
    if (node.italic) t = `<em>${t}</em>`;
    if (node.bold) t = `<strong>${t}</strong>`;
    if (node.underline) t = `<u>${t}</u>`;
    if (node.strikethrough) t = `<s>${t}</s>`;
    return t;
  };

  const richTextChildrenToHtml = (children) => {
    if (!children?.length) return "";
    let html = "";
    for (const child of children) {
      if (child.type === "text") {
        html += richTextTextNodeToHtml(child);
      } else if (child.type === "paragraph") {
        const inner = richTextChildrenToHtml(child.children);
        const ch = child.children;
        const onlyHeading =
          Array.isArray(ch) && ch.length === 1 && ch[0]?.type === "heading";
        if (onlyHeading) html += inner;
        else if (inner) html += `<p>${inner}</p>`;
      } else if (child.type === "heading") {
        const lvl = Math.min(
          6,
          Math.max(1, parseInt(String(child.level), 10) || 3),
        );
        html += `<h${lvl}>${richTextChildrenToHtml(child.children)}</h${lvl}>`;
      } else if (child.type === "list") {
        const tag = child.listType === "ordered" ? "ol" : "ul";
        html += `<${tag}>${richTextChildrenToHtml(child.children)}</${tag}>`;
      } else if (child.type === "list-item") {
        html += `<li>${richTextChildrenToHtml(child.children)}</li>`;
      } else if (child.type === "link") {
        const href = escapeHtml(child.url || "#");
        const titleAttr =
          typeof child.title === "string" && child.title.trim()
            ? ` title="${escapeHtml(child.title.trim())}"`
            : "";
        const blank =
          child.target === "_blank"
            ? ' target="_blank" rel="noopener noreferrer"'
            : ' rel="noopener noreferrer"';
        html += `<a href="${href}"${titleAttr}${blank}>${richTextChildrenToHtml(child.children)}</a>`;
      } else if (child.type === "bold") {
        html += `<strong>${richTextChildrenToHtml(child.children)}</strong>`;
      } else if (child.type === "italic") {
        html += `<em>${richTextChildrenToHtml(child.children)}</em>`;
      } else if (child.type === "blockquote") {
        html += `<blockquote>${richTextChildrenToHtml(child.children)}</blockquote>`;
      } else if (
        child.type === "line-break" ||
        child.type === "linebreak" ||
        child.type === "break"
      ) {
        html += "<br>";
      } else if (child?.children?.length) {
        html += richTextChildrenToHtml(child.children);
      }
    }
    return html;
  };

  const convertRichMetafieldValue = (value) => {
    if (value == null || value === "") return "";
    try {
      const j = JSON.parse(value);
      if (j?.type === "root" && j.children)
        return richTextChildrenToHtml(j.children);
    } catch (e) {
      /* ignore */
    }
    return escapeHtml(value).replace(/\n/g, "<br>");
  };

  const metafieldByKey = (location, key) => {
    const list = location.metafields;
    if (!list?.length) return null;
    return list.find((m) => m?.key === key) || null;
  };

  /**
   * URL from location metafield `location_url` (Storefront page reference, namespace from theme settings).
   */
  const locationPageUrlFromLocation = (location) => {
    const mf = metafieldByKey(location, "location_url");
    const ref = mf?.reference;
    if (!ref || typeof ref !== "object") return "";
    const direct = ref.onlineStoreUrl || ref.url;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    const h = typeof ref.handle === "string" ? ref.handle.trim() : "";
    if (h && ref.__typename === "Page") {
      const root = String(window.Shopify?.routes?.root || "/").replace(
        /\/?$/,
        "",
      );
      return `${root || ""}/pages/${encodeURIComponent(h)}`;
    }
    return "";
  };

  const storefrontFetch = (query, variables) => {
    const token =
      typeof window.storefront?.token === "string"
        ? window.storefront.token.trim()
        : "";
    if (!window.storefront?.url || !token) return Promise.resolve(null);
    return fetch(window.storefront.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query, variables: variables || {} }),
    })
      .then(async (r) => {
        let json = null;
        try {
          json = await r.json();
        } catch (e) {
          console.warn("[store-selector] Storefront response was not JSON", e);
          return null;
        }
        if (!r.ok) {
          console.warn("[store-selector] Storefront HTTP", r.status, json);
          return null;
        }
        if (json.errors?.length) {
          console.warn("[store-selector] GraphQL errors", json.errors);
        }
        if (!json.data && json.errors?.length) {
          return null;
        }
        return json.data || null;
      })
      .catch((e) => {
        console.warn("[store-selector]", e);
        return null;
      });
  };

  /**
   * Core pickup locations only (no metafields). The batched `metafields(identifiers: …)` selection
   * can cause the whole operation to return errors when those keys are not exposed to Storefront
   * (metafield definitions need access.storefront PUBLIC_READ for LOCATION). Optional metafields are
   * loaded per-field with aliases so missing exposure only nulls those fields, not the location list.
   */
  const locationsQuery = (ns) => {
    const safe = String(ns || "custom").replace(/[^a-zA-Z0-9_]/g, "");
    return `query StoreSelectorLocations {
      locations(first: 50) {
        nodes {
          id
          name
          address {
            address1
            address2
            city
            province
            provinceCode
            zip
            phone
            country
            latitude
            longitude
          }
          mf_email: metafield(namespace: "${safe}", key: "store_email") {
            namespace
            key
            value
            type
          }
          mf_instagram: metafield(namespace: "${safe}", key: "store_instagram") {
            namespace
            key
            value
            type
          }
          mf_hours: metafield(namespace: "${safe}", key: "store_hours") {
            namespace
            key
            value
            type
          }
          mf_location_hours: metafield(namespace: "${safe}", key: "location_hours") {
            namespace
            key
            value
            type
          }
          mf_location_url: metafield(namespace: "${safe}", key: "location_url") {
            namespace
            key
            type
            reference {
              ... on Page {
                __typename
                handle
                onlineStoreUrl
              }
            }
          }
        }
      }
    }`;
  };

  const normalizeLocationNode = (node) => {
    if (!node || typeof node !== "object") return node;
    if (Array.isArray(node.metafields)) return node;
    const mfs = [
      node.mf_email,
      node.mf_instagram,
      node.mf_hours,
      node.mf_location_hours,
      node.mf_location_url,
    ].filter(Boolean);
    const {
      mf_email,
      mf_instagram,
      mf_hours,
      mf_location_hours,
      mf_location_url,
      ...rest
    } = node;
    return { ...rest, metafields: mfs };
  };

  /** Pickup locations only — no metafields (safest if optional metafield selections fail validation). */
  const locationsQueryCoreOnly = () => `query StoreSelectorLocationsCore {
    locations(first: 50) {
      nodes {
        id
        name
        address {
          address1
          address2
          city
          province
          provinceCode
          zip
          phone
          country
          latitude
          longitude
        }
      }
    }
  }`;

  const variantsByIdsQuery = () =>
    `query StoreSelectorVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          product { title }
          requiresShipping
          storeAvailability(first: 50) {
            nodes {
              available
              location { id }
            }
          }
        }
      }
    }`;

  const toVariantGids = (ids) => {
    const out = [];
    const seen = {};
    for (const raw of ids) {
      if (raw == null) continue;
      const n = String(raw).replace(/\D/g, "");
      if (!n || seen[n]) continue;
      seen[n] = 1;
      out.push(`gid://shopify/ProductVariant/${n}`);
    }
    return out;
  };

  const getCart = () => {
    const base = window.Shopify?.routes?.root || "/";
    let url = `${base.replace(/\/?$/, "/")}cart.js`;
    if (window.routes?.cart_url) {
      url = `${window.routes.cart_url.replace(/\/?$/, "")}.js`;
    }
    return fetch(url, { credentials: "same-origin" }).then((r) => r.json());
  };

  const cartUpdateJsUrl = () => {
    const base = window.Shopify?.routes?.root || "/";
    if (window.routes?.cart_url) {
      return `${window.routes.cart_url.replace(/\/?$/, "")}/update.js`;
    }
    return `${base.replace(/\/?$/, "/")}cart/update.js`;
  };

  /**
   * Normalizes strings for cart attributes: tabs/newlines → spaces, trim,
   * collapse runs of whitespace (avoids odd JSON like tab between street and city).
   */
  const sanitizeCartAttributeString = (v) => {
    if (v == null) return "";
    return String(v).replace(/\s+/g, " ").trim();
  };

  /** Checkout-safe subset (cart attributes are strings; keep payload small). */
  const compactLocationForCartAttribute = (location) => {
    if (!location || typeof location !== "object") return null;
    const a = location.address || {};
    return {
      id: location.id,
      name: sanitizeCartAttributeString(location.name),
      address1: sanitizeCartAttributeString(a.address1),
      address2: sanitizeCartAttributeString(a.address2),
      city: sanitizeCartAttributeString(a.city),
      province: sanitizeCartAttributeString(a.province),
      provinceCode: sanitizeCartAttributeString(a.provinceCode),
      zip: sanitizeCartAttributeString(a.zip),
      country: sanitizeCartAttributeString(a.country),
      phone: sanitizeCartAttributeString(a.phone),
    };
  };

  /**
   * Plain cart attribute for Delivery Customization / checkout Functions: canonical
   * Storefront Location `id` (GID string). Query in Functions as
   * `cart { preferredId: attribute(key: "preferred_pickup_location_id") { value } }`.
   */
  const PREFERRED_PICKUP_LOCATION_CART_KEY = "preferred_pickup_location_id";

  /**
   * Persists the chosen pickup location to cart attributes:
   * - `selected_location` — JSON for orders / admin (existing)
   * - `preferred_pickup_location_id` — Location GID string for checkout Functions (reorder / default pickup)
   *
   * Always GET /cart.js first and merge into `attributes` so other keys are never dropped.
   * After POST, GET /cart.js again to confirm both keys match; one retry on mismatch.
   *
   * Disable with `window.storeSelector.persistSelectedStoreToCart = false`.
   * @returns {Promise<void>}
   */
  const verifyCartLocationAttributes = (compact) => {
    const wantId = String(compact.id).trim();
    return getCart().then((cart) => {
      const a = cart.attributes || {};
      const pref = String(a[PREFERRED_PICKUP_LOCATION_CART_KEY] || "").trim();
      if (pref !== wantId) return false;
      try {
        const o = JSON.parse(a.selected_location || "{}");
        return String(o?.id || "").trim() === wantId;
      } catch {
        return false;
      }
    });
  };

  const postLocationAttributesToCart = (compact, payload) =>
    getCart().then((cart) => {
      const attrs = { ...(cart.attributes || {}) };
      attrs.selected_location = payload;
      attrs[PREFERRED_PICKUP_LOCATION_CART_KEY] = String(compact.id);
      return fetch(cartUpdateJsUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ attributes: attrs }),
      });
    });

  const syncSelectedLocationToCart = (location) => {
    if (window.storeSelector?.persistSelectedStoreToCart === false)
      return Promise.resolve();
    let compact = compactLocationForCartAttribute(location);
    if (!compact?.id) return Promise.resolve();
    let payload = JSON.stringify(compact);
    const maxLen = 4500;
    while (payload.length > maxLen) {
      compact = { id: compact.id, name: (compact.name || "").slice(0, 120) };
      payload = JSON.stringify(compact);
      if (payload.length > maxLen) {
        compact = { id: compact.id };
        payload = JSON.stringify(compact);
        break;
      }
    }

    const runOnce = () =>
      postLocationAttributesToCart(compact, payload).then((res) => {
        if (!res.ok) throw new Error(`cart/update.js ${res.status}`);
      });

    return runOnce()
      .then(() => verifyCartLocationAttributes(compact))
      .then((ok) => {
        if (ok) return undefined;
        return runOnce();
      })
      .then(() => verifyCartLocationAttributes(compact))
      .then((ok) => {
        if (!ok) {
          console.warn(
            "[store-selector] Cart attributes did not match after sync; check Network tab for /cart/update.js",
          );
        }
      })
      .catch((e) => {
        console.warn(
          "[store-selector] Could not save selected location to cart attributes",
          e,
        );
      });
  };

  /**
   * If `localStorage` has a selected store but the cart is missing or mismatched
   * `preferred_pickup_location_id` / `selected_location` JSON id, POST /cart/update.js once.
   * Exposed as `window.storeSelectorSyncPreferredPickupFromStorage`.
   * @returns {Promise<void>}
   */
  const syncPreferredPickupToCartFromLocalStorage = () => {
    if (window.storeSelector?.persistSelectedStoreToCart === false)
      return Promise.resolve();
    let location = null;
    try {
      const raw = window.localStorage.getItem("selected-location");
      if (!raw) return Promise.resolve();
      location = JSON.parse(raw);
    } catch {
      return Promise.resolve();
    }
    if (!location || typeof location !== "object" || !location.id)
      return Promise.resolve();
    const gid = String(location.id);
    return getCart()
      .then((cart) => {
        const attrs = cart.attributes || {};
        const pref = String(
          attrs[PREFERRED_PICKUP_LOCATION_CART_KEY] || "",
        ).trim();
        const gidNorm = String(gid).trim();
        let innerIdMatches = false;
        if (attrs.selected_location) {
          try {
            const o = JSON.parse(attrs.selected_location);
            innerIdMatches = String(o?.id || "").trim() === gidNorm;
          } catch {
            innerIdMatches = false;
          }
        }
        if (pref === gidNorm && innerIdMatches) return undefined;
        return syncSelectedLocationToCart(location);
      })
      .catch(() => { });
  };

  /**
   * Always POST `selected_location` + `preferred_pickup_location_id` from
   * `localStorage` (used before native checkout submit so the cart cannot lag
   * behind a recent store change).
   * @returns {Promise<void>}
   */
  const flushCartLocationFromLocalStorageToCart = () => {
    if (window.storeSelector?.persistSelectedStoreToCart === false)
      return Promise.resolve();
    let location = null;
    try {
      const raw = window.localStorage.getItem("selected-location");
      if (!raw) return Promise.resolve();
      location = JSON.parse(raw);
    } catch {
      return Promise.resolve();
    }
    if (!location || typeof location !== "object" || !location.id)
      return Promise.resolve();
    return syncSelectedLocationToCart(location);
  };

  if (typeof window !== "undefined") {
    window.storeSelectorSyncPreferredPickupFromStorage =
      syncPreferredPickupToCartFromLocalStorage;
    window.storeSelectorFlushCartLocationFromStorageForCheckout =
      flushCartLocationFromLocalStorageToCart;
  }

  /**
   * Before cart → checkout POST, ensure latest `selected-location` is on the
   * cart (avoids race: drawer closes before /cart/update.js completes).
   */
  let _storeSelectorBypassCheckoutCapture = false;
  if (typeof document !== "undefined") {
    document.addEventListener(
      "submit",
      (e) => {
        if (_storeSelectorBypassCheckoutCapture) return;
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (window.storeSelector?.persistSelectedStoreToCart === false) return;
        if (!form.querySelector('[name="checkout"]')) return;

        let hasLocation = false;
        try {
          const raw = window.localStorage.getItem("selected-location");
          if (raw) {
            const loc = JSON.parse(raw);
            hasLocation = Boolean(loc && typeof loc === "object" && loc.id);
          }
        } catch {
          hasLocation = false;
        }
        if (!hasLocation) return;

        const fid = form.getAttribute("id");
        const rawSubmitter = e.submitter;
        const submitter =
          rawSubmitter instanceof HTMLElement &&
            (form.contains(rawSubmitter) ||
              (fid &&
                rawSubmitter.getAttribute("form") === fid &&
                rawSubmitter.getAttribute("name") === "checkout"))
            ? rawSubmitter
            : null;

        e.preventDefault();
        e.stopImmediatePropagation();

        flushCartLocationFromLocalStorageToCart()
          .catch(() => { })
          .finally(() => {
            _storeSelectorBypassCheckoutCapture = true;
            try {
              const btn =
                submitter ||
                form.querySelector(
                  'button[type="submit"][name="checkout"], input[type="submit"][name="checkout"]',
                );
              if (btn && typeof form.requestSubmit === "function")
                form.requestSubmit(btn);
              else if (typeof form.requestSubmit === "function")
                form.requestSubmit();
              else HTMLFormElement.prototype.submit.call(form);
            } catch {
              HTMLFormElement.prototype.submit.call(form);
            } finally {
              _storeSelectorBypassCheckoutCapture = false;
            }
          });
      },
      true,
    );
  }

  /** After checkout → shop: immediate LS → cart sync. Tab focus uses debounce. */
  let storeSelectorVisibilityFlushTimer = 0;
  if (typeof window !== "undefined") {
    window.addEventListener("pageshow", () => {
      if (window.storeSelector?.persistSelectedStoreToCart === false) return;
      syncPreferredPickupToCartFromLocalStorage();
    });
  }
  if (typeof document !== "undefined") {
    const flushCartFromLsDebounced = () => {
      if (window.storeSelector?.persistSelectedStoreToCart === false) return;
      window.clearTimeout(storeSelectorVisibilityFlushTimer);
      storeSelectorVisibilityFlushTimer = window.setTimeout(() => {
        syncPreferredPickupToCartFromLocalStorage();
      }, 250);
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") flushCartFromLsDebounced();
    });
  }

  const STORE_SELECTOR_PASSIVE_GEO_KEY = "store-selector-passive-geo";
  const STORE_SELECTOR_PASSIVE_GEO_MAX_MS = 5 * 60 * 1000;

  /** Approximate visitor position from IP (browser fetch); used when proximity gate needs coords but GPS/localStorage are unavailable. */
  const STORE_SELECTOR_IP_GEO_KEY = "store-selector-ip-geo";
  const STORE_SELECTOR_IP_GEO_MAX_MS = 24 * 60 * 60 * 1000;

  const formatMilesFromMeters = (meters) => {
    if (meters == null || !Number.isFinite(meters) || meters < 0) return "";
    const mi = meters / 1609.344;
    if (mi < 0.05) return "0.1 mi";
    const rounded = mi < 10 ? mi.toFixed(1) : String(Math.round(mi));
    return `${rounded} mi`;
  };

  const parseUserLocationCoords = () => {
    try {
      const raw = window.localStorage.getItem("user-location");
      if (!raw) return null;
      const u = JSON.parse(raw);
      const lat = parseFloat(u.latitude);
      const lng = parseFloat(u.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    } catch (e) {
      return null;
    }
  };

  const parsePassiveGeoCoords = () => {
    try {
      const raw = window.sessionStorage.getItem(STORE_SELECTOR_PASSIVE_GEO_KEY);
      if (!raw) return null;
      const o = JSON.parse(raw);
      const at = typeof o.at === "number" ? o.at : 0;
      if (Date.now() - at > STORE_SELECTOR_PASSIVE_GEO_MAX_MS) return null;
      const lat = parseFloat(o.latitude);
      const lng = parseFloat(o.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    } catch (e) {
      return null;
    }
  };

  const parseIpGeoCoords = () => {
    try {
      const raw = window.sessionStorage.getItem(STORE_SELECTOR_IP_GEO_KEY);
      if (!raw) return null;
      const o = JSON.parse(raw);
      const at = typeof o.at === "number" ? o.at : 0;
      if (Date.now() - at > STORE_SELECTOR_IP_GEO_MAX_MS) return null;
      const lat = parseFloat(o.latitude);
      const lng = parseFloat(o.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    } catch (e) {
      return null;
    }
  };

  /** Saved search / “Use my current location” first; then short-lived GPS; then IP-based approximate location. */
  const referenceCoordsForStoreDistance = () =>
    parseUserLocationCoords() || parsePassiveGeoCoords() || parseIpGeoCoords();

  const distanceMetersFromUserLocation = (location) => {
    const ref = referenceCoordsForStoreDistance();
    if (!ref) return null;
    try {
      const a = location?.address || {};
      const lat2 = parseFloat(a.latitude);
      const lng2 = parseFloat(a.longitude);
      if (Number.isNaN(lat2) || Number.isNaN(lng2)) return null;
      return haversineMeters(ref.lat, ref.lng, lat2, lng2);
    } catch (e) {
      return null;
    }
  };

  const formatPhoneNumber = (phoneNumberString) => {
    const formatted = `${phoneNumberString}`.replace(/\D/g, "");
    const match = formatted.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      const intlCode = match[1] ? "+1 " : "";
      return `${intlCode}${match[2]}-${match[3]}-${match[4]}`;
    }
    return phoneNumberString;
  };

  const strCfg = (key, fallback) => {
    const o = window.storeSelector || {};
    return o[key] != null && o[key] !== "" ? o[key] : fallback;
  };

  const cleanStoreName = (name) => {
    const n = String(name || "")
      .replace(/_\d+$/, "")
      .trim(); // strip trailing _<id>
    return /^Lauderhill Clearance$/i.test(n) ? "Clearance Outlet" : n;
  };

  const storeDisplayLabel = (location) => {
    const cleaned = cleanStoreName(location?.name);
    if (cleaned) return cleaned;

    const a = location?.address || {};
    const city = String(a.city || "").trim();
    const region = String(a.provinceCode || a.province || "").trim();
    return [city, region].filter(Boolean).join(", ");
  };

  /** Shortest great-circle distance from the visitor to any location with valid lat/lng (nearest store). */
  const storeSelectorMinDistanceMeters = (userLat, userLng, locations) => {
    let min = Infinity;
    if (!locations?.length) return min;
    for (const loc of locations) {
      const a = loc.address;
      if (!a) continue;
      const la = parseFloat(a.latitude);
      const lo = parseFloat(a.longitude);
      if (Number.isNaN(la) || Number.isNaN(lo)) continue;
      const d = haversineMeters(userLat, userLng, la, lo);
      if (d < min) min = d;
    }
    return min;
  };

  let passiveGeoRequestInFlight = false;
  let ipGeoRequestInFlight = false;

  /**
   * Approximate lat/lng from visitor IP (public API, no key). Cached in sessionStorage;
   * updates header when Theme → “Only show header store locator within max distance” is on.
   */
  function beginIpGeoIfProximityGate() {
    if (window.storeSelector?.proximityGateEnabled !== true) return;
    if (parseUserLocationCoords() || parsePassiveGeoCoords()) return;
    if (parseIpGeoCoords()) {
      updateStoreSelectorHeaderVisibility();
      return;
    }
    if (ipGeoRequestInFlight) return;
    if (typeof fetch !== "function") return;
    ipGeoRequestInFlight = true;
    fetch("https://ipwho.is/", {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((data) => {
        ipGeoRequestInFlight = false;
        if (!data || data.success === false) return;
        const lat = parseFloat(data.latitude);
        const lng = parseFloat(data.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        try {
          window.sessionStorage.setItem(
            STORE_SELECTOR_IP_GEO_KEY,
            JSON.stringify({
              latitude: lat,
              longitude: lng,
              at: Date.now(),
            }),
          );
        } catch (e) {
          /* ignore */
        }
        updateStoreSelectorHeaderVisibility();
        document.dispatchEvent(
          new CustomEvent("buildLocationsList", {
            bubbles: true,
            detail: { skipInventoryRefresh: true },
          }),
        );
      })
      .catch(() => {
        ipGeoRequestInFlight = false;
      });
  }

  /**
   * Device coords for distances / proximity when the visitor has not saved a search (see Theme →
   * Store selector). Throttled; updates header visibility after success.
   */
  function beginPassiveGeoIfNoUserLocation() {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    if (parseUserLocationCoords()) return;
    if (parsePassiveGeoCoords() || parseIpGeoCoords()) return;
    if (passiveGeoRequestInFlight) return;
    try {
      const raw = window.sessionStorage.getItem(STORE_SELECTOR_PASSIVE_GEO_KEY);
      if (raw) {
        const o = JSON.parse(raw);
        if (
          typeof o?.at === "number" &&
          Date.now() - o.at < STORE_SELECTOR_PASSIVE_GEO_MAX_MS
        )
          return;
      }
    } catch (e) {
      /* ignore */
    }
    passiveGeoRequestInFlight = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        passiveGeoRequestInFlight = false;
        try {
          window.sessionStorage.setItem(
            STORE_SELECTOR_PASSIVE_GEO_KEY,
            JSON.stringify({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              at: Date.now(),
            }),
          );
        } catch (e) {
          /* ignore */
        }
        document.dispatchEvent(
          new CustomEvent("buildLocationsList", {
            bubbles: true,
            detail: { skipInventoryRefresh: true },
          }),
        );
        updateStoreSelectorHeaderVisibility();
      },
      () => {
        passiveGeoRequestInFlight = false;
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 120000,
      },
    );
  }

  /**
   * Header store locator visibility (Theme → Store selector → “Only show header store locator
   * within max distance”, plus `data-store-locator-proximity-only` from the same setting in Liquid).
   * - Gate off: always show the pin when the wrap exists (header “Show store locator” is on).
   * - Gate on: show only when the visitor is within headerToggleMaxMeters of **at least one**
   *   pickup location that has lat/lng (we use distance to the **nearest** such store). Hide when
   *   they are outside that radius of **every** located store (nearest distance > max). Hide if no
   *   location has coordinates. Requests passive geolocation and/or IP lookup when position is
   *   not yet known.
   */
  const updateStoreSelectorHeaderVisibility = () => {
    const wraps = document.querySelectorAll(
      "[data-store-selector-header-toggle]",
    );
    if (!wraps.length) return;
    const proximityOnly = window.storeSelector?.proximityGateEnabled === true;
    const el = document.querySelector("store-selector");
    const locations = Array.isArray(el?.locations) ? el.locations : [];
    const maxM =
      window.storeSelector?.headerToggleMaxMeters > 0
        ? window.storeSelector.headerToggleMaxMeters
        : 160934.4;
    const ref = referenceCoordsForStoreDistance();
    const lat = ref ? ref.lat : NaN;
    const lng = ref ? ref.lng : NaN;
    let show = false;
    if (!proximityOnly) {
      show = true;
    } else if (!locations.length) {
      show = false;
    } else if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      const minD = storeSelectorMinDistanceMeters(lat, lng, locations);
      /* Infinity = no store had usable coordinates → cannot be “within range” of any store */
      if (minD === Infinity) {
        show = false;
      } else {
        show = minD <= maxM;
      }
    } else {
      /* No position yet (and proximity gate on): keep hidden until GPS or IP geo resolves. */
      show = false;
    }
    wraps.forEach((w) => {
      if (show) {
        w.hidden = false;
        w.removeAttribute("hidden");
      } else {
        w.hidden = true;
        w.setAttribute("hidden", "");
      }
      w.setAttribute("aria-hidden", show ? "false" : "true");
      w.classList.toggle("store-selector-header-wrap--visible", show);
    });
    if (show) {
      document.querySelectorAll("store-selector-toggle").forEach((toggle) => {
        if (typeof toggle.updateButtonText === "function")
          toggle.updateButtonText();
      });
    }
    if (
      proximityOnly &&
      locations.length > 0 &&
      !referenceCoordsForStoreDistance()
    ) {
      beginPassiveGeoIfNoUserLocation();
      beginIpGeoIfProximityGate();
    }
  };

  window.updateStoreSelectorHeaderVisibility =
    updateStoreSelectorHeaderVisibility;

  window.addEventListener("load", () => {
    updateStoreSelectorHeaderVisibility();
  });

  const onDomReady = (fn) => {
    if (document.readyState === "loading")
      document.addEventListener("DOMContentLoaded", fn);
    else fn();
  };

  if (
    typeof window !== "undefined" &&
    typeof window.gm_authFailure !== "function"
  ) {
    window.gm_authFailure = function storeSelectorGmAuthFailure() {
      console.warn(
        "[store-selector] Google Maps could not authenticate this page. In Google Cloud Console: enable billing on the project; enable Maps JavaScript API and Places API; under API key restrictions allow HTTP referrers for your storefront (e.g. https://your-domain.com/* and https://*.myshopify.com/*). Theme setting: Store selector → Google Maps API key.",
      );
    };
  }

  let _storeSelectorGmapsScriptPromise = null;

  function ensureStoreSelectorGoogleMapsScript() {
    if (typeof window === "undefined") return Promise.resolve();
    if (window.google?.maps) return Promise.resolve();
    const key =
      typeof window.storeSelector?.googleMapsKey === "string"
        ? window.storeSelector.googleMapsKey.trim()
        : "";
    if (!key) return Promise.resolve();
    if (_storeSelectorGmapsScriptPromise)
      return _storeSelectorGmapsScriptPromise;
    _storeSelectorGmapsScriptPromise = new Promise((resolve) => {
      const existing = document.querySelector(
        "script[data-store-selector-google-maps]",
      );
      if (existing) {
        if (window.google?.maps) {
          queueMicrotask(resolve);
          return;
        }
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => resolve(), { once: true });
        return;
      }
      const s = document.createElement("script");
      s.async = true;
      s.defer = true;
      s.dataset.storeSelectorGoogleMaps = "";
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
      s.onload = () => resolve();
      s.onerror = () => {
        console.warn(
          "[store-selector] Google Maps script failed to load (network, CSP, or blocked URL).",
        );
        s.remove();
        _storeSelectorGmapsScriptPromise = null;
        resolve();
      };
      document.head.appendChild(s);
    });
    return _storeSelectorGmapsScriptPromise;
  }

  const formatAddressForStoreGeocode = (address) => {
    if (!address || typeof address !== "object") return "";
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.provinceCode || address.province,
      address.zip,
      address.country,
    ].filter((p) => p != null && String(p).trim() !== "");
    return parts.join(", ").trim();
  };

  /** Opens Google Maps search for the location address in a new tab. */
  const googleMapsSearchUrlFromAddress = (address) => {
    const q = formatAddressForStoreGeocode(address);
    if (!q) return "";
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };

  const locationMissingLatLng = (location) => {
    const a = location?.address || {};
    const la = parseFloat(a.latitude);
    const lo = parseFloat(a.longitude);
    return Number.isNaN(la) || Number.isNaN(lo);
  };

  /**
   * Sets address.latitude / longitude via Google Geocoder when Storefront omits them (still lists address lines).
   * Requires Store selector → Google Maps API key and Geocoding API enabled for that project/key.
   */
  function enrichLocationsWithGeocodedAddresses(locations) {
    if (!Array.isArray(locations) || !locations.length)
      return Promise.resolve();
    const toGeocode = locations.filter(
      (loc) =>
        locationMissingLatLng(loc) &&
        formatAddressForStoreGeocode(loc.address).length > 0,
    );
    if (!toGeocode.length) return Promise.resolve();
    return ensureStoreSelectorGoogleMapsScript()
      .then(() => {
        if (!window.google?.maps?.Geocoder) return;
        const geocoder = new google.maps.Geocoder();
        return toGeocode.reduce(
          (chain, loc) =>
            chain.then(
              () =>
                new Promise((resolve) => {
                  const q = formatAddressForStoreGeocode(loc.address);
                  if (!q) {
                    resolve();
                    return;
                  }
                  geocoder.geocode({ address: q }, (results, status) => {
                    try {
                      if (status === "OK" && results?.[0]?.geometry?.location) {
                        const ll = results[0].geometry.location;
                        if (!loc.address) loc.address = {};
                        loc.address.latitude = ll.lat();
                        loc.address.longitude = ll.lng();
                      }
                    } catch (e) {
                      /* ignore */
                    }
                    resolve();
                  });
                }),
            ),
          Promise.resolve(),
        );
      })
      .catch(() => { });
  }

  if (!window.customElements.get("store-selector")) {
    class StoreSelector extends HTMLElement {
      constructor() {
        super();
        this.locations = [];
        this.input = null;
        this.currentLocationButton = null;
        this.useCurrentLocationText = null;
        this.locationsCount = null;
        this.locationsCountText = null;
        this.locationsList = null;
        this._searchBasicsBound = false;
        this._placesAutocomplete = null;
        this._searchFilter = "";
        /** Cached Storefront cart availability + PDP variant availability; skip re-fetch on search-only redraws. */
        this._drawerListSnapshot = null;
        this._autoSelectingNearestLocation = false;
      }

      clearDrawerListSnapshot() {
        this._drawerListSnapshot = null;
      }

      /**
       * Renders the location list using already-fetched inventory (`productVariant`, `cartVariantInventoryLevels`).
       * Reads `this.locations`, `_searchFilter`, and localStorage for selection / distances.
       */
      _applyLocationsListDom(
        selectedLocation,
        locationDistances,
        productVariant,
        cartVariantInventoryLevels,
      ) {
        if (!this.locationsList) {
          this.buildLocationsCount();
          return;
        }

        this.locationsList.innerHTML = "";

        /** Drawer lists every pickup location (sorted by distance when known). Max-distance applies only to header pin visibility, not this list. */
        const ref = referenceCoordsForStoreDistance();
        const ordered = [];
        if (locationDistances) {
          locationDistances.forEach((ld) => {
            const matchingLocation = this.locations.find((l) => l.id === ld.id);
            if (!matchingLocation) return;
            ordered.push({ location: matchingLocation, distance: ld.distance });
          });
        } else {
          this.locations.forEach((location, index) => {
            if (!location) return;
            if (!this.locationMatchesSearchFilter(location)) return;
            const a = location.address || {};
            const lat = parseFloat(a.latitude);
            const lng = parseFloat(a.longitude);
            const distance =
              ref && !Number.isNaN(lat) && !Number.isNaN(lng)
                ? haversineMeters(ref.lat, ref.lng, lat, lng)
                : null;
            ordered.push({ location, distance, index });
          });
        }

        ordered
          .filter((item) => this.locationMatchesSearchFilter(item.location))
          .sort((a, b) => {
            const ad = a.distance;
            const bd = b.distance;
            const aKnown = Number.isFinite(ad);
            const bKnown = Number.isFinite(bd);
            if (aKnown && bKnown) return ad - bd;
            if (aKnown) return -1;
            if (bKnown) return 1;
            return (a.index ?? 0) - (b.index ?? 0);
          })
          .forEach((item) => {
            this.addLocation(
              item.location,
              selectedLocation,
              productVariant,
              cartVariantInventoryLevels,
              item.distance,
            );
          });

        this.buildLocationsCount();
      }

      locationMatchesSearchFilter(location) {
        const q = (this._searchFilter || "").trim().toLowerCase();
        if (!q) return true;
        const name = (location?.name || "").toLowerCase();
        const a = location?.address || {};
        const blob = [
          name,
          a.address1,
          a.address2,
          a.city,
          a.province,
          a.provinceCode,
          a.zip,
          a.country,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      }

      /** Clears search UI and persisted geolocation used for distance sorting (`user-location`, `location-distances`). */
      clearAddressSearch() {
        this.clearAddressSearchError();
        this._searchFilter = "";
        if (this.input) this.input.value = "";
        try {
          window.localStorage.removeItem("user-location");
          window.localStorage.removeItem("location-distances");
        } catch (e) {
          /* ignore */
        }
        document.dispatchEvent(new Event("calculateDistances"));
        this.buildLocationsCount();
        this.buildLocationsList();
        updateStoreSelectorHeaderVisibility();
      }

      clearAddressSearchError() {
        if (this.searchErrorEl) {
          this.searchErrorEl.textContent = "";
          this.searchErrorEl.hidden = true;
        }
        if (this.input) {
          this.input.removeAttribute("aria-invalid");
          this.input.removeAttribute("aria-describedby");
        }
      }

      /**
       * Surfaces address-search failures (no Maps key, script load, geocode) for a11y and integrations.
       * Dispatches `store-selector:address-search-error` and legacy `storeSelectorError` with the same detail.
       */
      reportAddressSearchError(message, detail = {}) {
        const msg =
          typeof message === "string" && message.trim() !== ""
            ? message
            : strCfg("addressGeocodeFailed", "Could not resolve that address.");
        const payload = {
          bubbles: true,
          composed: true,
          detail: { message: msg, input: this.input, ...detail },
        };
        document.dispatchEvent(
          new CustomEvent("store-selector:address-search-error", payload),
        );
        document.dispatchEvent(new CustomEvent("storeSelectorError", payload));
        if (this.searchErrorEl) {
          this.searchErrorEl.textContent = msg;
          this.searchErrorEl.hidden = false;
        }
        if (this.input) {
          this.input.setAttribute("aria-invalid", "true");
          this.input.setAttribute(
            "aria-describedby",
            "store-selector-search-error",
          );
        }
      }

      submitAddressSearch() {
        this.clearAddressSearchError();
        const q = (this.input?.value || "").trim();
        if (!q) return;
        const key =
          typeof window.storeSelector?.googleMapsKey === "string"
            ? window.storeSelector.googleMapsKey.trim()
            : "";
        const done = (lat, lng, name) => {
          this.clearAddressSearchError();
          this._searchFilter = "";
          window.localStorage.setItem(
            "user-location",
            JSON.stringify({
              latitude: lat,
              longitude: lng,
              name: name || q,
            }),
          );
          if (this.input) this.input.value = name || q;
          document.dispatchEvent(new Event("calculateDistances"));
        };
        if (window.google?.maps?.Geocoder) {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ address: q }, (results, status) => {
            if (status !== "OK" || !results?.[0]?.geometry?.location) {
              console.warn("[store-selector] Geocode", status, results);
              this.reportAddressSearchError(
                strCfg(
                  "addressGeocodeFailed",
                  "Could not find that address. Try again.",
                ),
                { reason: "geocode", geocodeStatus: status },
              );
              return;
            }
            const loc = results[0].geometry.location;
            done(
              loc.lat(),
              loc.lng(),
              results[0].formatted_address || results[0].name || q,
            );
          });
          return;
        }
        if (key) {
          this.loadGoogleMaps(() => {
            if (window.google?.maps?.Geocoder) {
              this.submitAddressSearch();
              return;
            }
            this.reportAddressSearchError(
              strCfg(
                "addressMapsLoadFailed",
                "Maps could not load. Check your connection or API key.",
              ),
              { reason: "maps_load" },
            );
          });
          return;
        }
        this.reportAddressSearchError(
          strCfg(
            "addressSearchRequiresMaps",
            "Add a Google Maps API key in theme settings to search by address, or pick a store from the list.",
          ),
          { reason: "no_maps_key" },
        );
      }

      bindSearchBasicsOnce() {
        if (this._searchBasicsBound) return;
        this._searchBasicsBound = true;
        const clearBtn = this.querySelector("[data-store-selector-clear]");
        if (clearBtn) {
          clearBtn.addEventListener("click", () => this.clearAddressSearch());
        }
        const applyBtn = this.querySelector("[data-store-selector-apply]");
        if (applyBtn) {
          applyBtn.addEventListener("click", () => this.submitAddressSearch());
        }
        if (this.input) {
          let debounceTimer;
          this.input.addEventListener("input", () => {
            this.clearAddressSearchError();
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
              this._searchFilter = this.input.value;
              this.buildLocationsList();
            }, 200);
          });
          this.input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              this.submitAddressSearch();
            }
          });
        }
      }

      ensureGooglePlacesAutocomplete() {
        if (this._placesAutocomplete || !window.storeSelector?.googleMapsKey)
          return;
        if (!this.input) return;
        this.loadGoogleMaps(() => {
          if (!window.google?.maps?.places || !this.input) return;
          const country = window.storeSelector?.autocompleteCountry || "US";
          this._placesAutocomplete = new google.maps.places.Autocomplete(
            this.input,
            {
              componentRestrictions: {
                country: String(country).toLowerCase(),
              },
              fields: [
                "address_components",
                "geometry",
                "name",
                "formatted_address",
              ],
              strictBounds: false,
            },
          );
          google.maps.event.addListener(
            this._placesAutocomplete,
            "place_changed",
            () => {
              const place = this._placesAutocomplete.getPlace();
              if (!place.geometry?.location) return;
              this.clearAddressSearchError();
              window.localStorage.setItem(
                "user-location",
                JSON.stringify({
                  latitude: place.geometry.location.lat(),
                  longitude: place.geometry.location.lng(),
                  name:
                    place.formatted_address ||
                    place.name ||
                    this.input.value ||
                    "",
                }),
              );
              if (this.input) {
                this.input.value =
                  place.formatted_address ||
                  place.name ||
                  this.input.value ||
                  "";
              }
              this._searchFilter = "";
              document.dispatchEvent(new Event("calculateDistances"));
            },
          );
        });
      }

      scheduleSearchUiInit() {
        window.setTimeout(() => {
          this.ensureGooglePlacesAutocomplete();
        }, 150);
      }

      connectedCallback() {
        this.input = this.querySelector("[data-store-selector-input]");
        this.currentLocationButton = this.querySelector(
          "[data-store-selector-use-current-location]",
        );
        this.useCurrentLocationText = this.querySelector(
          "[data-store-selector-use-current-location-text]",
        );
        this.locationsCountRow = this.querySelector(
          "[data-store-selector-count-row]",
        );
        this.locationsCount = this.querySelector("[data-store-selector-count]");
        this.locationsCountText = this.querySelector(
          "[data-store-selector-count-text]",
        );
        this.locationsList = this.querySelector(
          "[data-store-selector-locations-list]",
        );
        this.emptyNearbyEl = this.querySelector(
          "[data-store-selector-empty-nearby]",
        );
        this.emptyNearbyText = this.querySelector(
          "[data-store-selector-empty-nearby-text]",
        );
        this.searchErrorEl = this.querySelector(
          "[data-store-selector-search-error]",
        );

        /* Bind clear / apply / input even when the panel is opened via openPanel() (no openStoreSelector). */
        this.bindSearchBasicsOnce();

        this._onGeolocateClick = (e) => {
          e.preventDefault();
          this.getCurrentLocation(false);
        };
        this._onGeolocateKeydown = (e) => {
          if (e.key !== " " && e.key !== "Spacebar") return;
          e.preventDefault();
          this.getCurrentLocation(false);
        };
        if (this.currentLocationButton) {
          this.currentLocationButton.addEventListener(
            "click",
            this._onGeolocateClick,
          );
          this.currentLocationButton.addEventListener(
            "keydown",
            this._onGeolocateKeydown,
          );
        }

        this._onCalc = () => {
          this.refreshDistanceOrdering();
          if (this.useCurrentLocationText)
            this.useCurrentLocationText.innerText = strCfg(
              "useCurrentLocation",
              "",
            );
        };
        this._onOpen = () => this.open();
        this._onBuild = (e) => {
          if (!e?.detail?.skipInventoryRefresh) this.clearDrawerListSnapshot();
          this.buildLocationsList();
        };
        /** Theme fires `listCart` on `window` after cart drawer / line items update (custom-async.js). */
        this._onListCart = () => {
          this.clearDrawerListSnapshot();
          syncPreferredPickupToCartFromLocalStorage();
          this.buildLocationsList();
        };
        this._onProductVariants = () => {
          this.clearDrawerListSnapshot();
          this.buildLocationsList();
        };
        this._onListClick = (event) => this._handleListClick(event);

        document.addEventListener("calculateDistances", this._onCalc);
        document.addEventListener("openStoreSelector", this._onOpen);
        document.addEventListener("buildLocationsList", this._onBuild);
        window.addEventListener("listCart", this._onListCart);
        window.addEventListener("productVariants", this._onProductVariants);
        this.addEventListener("click", this._onListClick);

        onDomReady(() => {
          /* Start passive location detection early so nearest-store auto-select
             can happen during page load instead of waiting for the CTA click. */
          beginPassiveGeoIfNoUserLocation();
          beginIpGeoIfProximityGate();
          syncPreferredPickupToCartFromLocalStorage();
          this.getLocations();
        });
      }

      disconnectedCallback() {
        if (this.currentLocationButton) {
          if (this._onGeolocateClick) {
            this.currentLocationButton.removeEventListener(
              "click",
              this._onGeolocateClick,
            );
          }
          if (this._onGeolocateKeydown) {
            this.currentLocationButton.removeEventListener(
              "keydown",
              this._onGeolocateKeydown,
            );
          }
        }
        document.removeEventListener("calculateDistances", this._onCalc);
        document.removeEventListener("openStoreSelector", this._onOpen);
        document.removeEventListener("buildLocationsList", this._onBuild);
        window.removeEventListener("listCart", this._onListCart);
        window.removeEventListener("productVariants", this._onProductVariants);
        this.removeEventListener("click", this._onListClick);
      }

      _handleListClick(event) {
        const btn = event.target.closest("[data-store-selector-store-select]");
        if (!btn || !this.contains(btn)) return;
        showBMLoader();
        const id = btn.getAttribute("data-location-id");
        const loc = this.locations.find((l) => l.id === id);
        if (!loc) return;
        this.selectLocation(loc);
        // Zeta tracking for store selection
        window._zetaFired ??= new Set();
        const customerFields = {};
        if (window.customerData?.isLoggedIn) {
          if (window.customerData.customerId) customerFields.customerID = window.customerData.customerId;
          if (window.customerData.customerEmail) customerFields.customerEmail = window.customerData.customerEmail;
        }
        bt('track', 'select_store', {
          id: loc.id,
          name: loc.name,
          address: loc.address ? `${loc.address.address1 || ''} ${loc.address.city || ''} ${loc.address.provinceCode || ''} ${loc.address.zip || ''}`.trim() : '',
          ...customerFields
        });
        const psd = document.querySelector("product-shipping-details");
        if (psd && typeof psd.checkBopisLocation === "function") {
          psd.checkBopisLocation();
          psd.checkInventory();
        }
        setTimeout(() => {
          hideBMLoader();
        }, 1500);
      }

      async _autoSelectNearestLocationIfNeeded() {
        if (this._autoSelectingNearestLocation) return false;
        let selectedLocation = null;
        try {
          selectedLocation = window.localStorage.getItem("selected-location")
            ? JSON.parse(window.localStorage.getItem("selected-location"))
            : null;
        } catch (e) {
          /* ignore */
        }
        if (selectedLocation?.id) return false;
        if (!this.locations?.length) return false;

        const ref = referenceCoordsForStoreDistance();
        if (!ref) return false;

        let nearestLocation = null;
        let nearestDistance = Infinity;
        for (const location of this.locations) {
          const a = location.address || {};
          const lat = parseFloat(a.latitude);
          const lng = parseFloat(a.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
          const d = haversineMeters(ref.lat, ref.lng, lat, lng);
          if (d < nearestDistance) {
            nearestDistance = d;
            nearestLocation = location;
          }
        }

        if (!nearestLocation) return false;

        this._autoSelectingNearestLocation = true;
        try {
          await this.selectLocation(nearestLocation);
          return true;
        } finally {
          this._autoSelectingNearestLocation = false;
        }
      }

      getLocations() {
        const ns = window.storeSelector?.metafieldNamespace || "custom";
        const finish = (data) => {
          try {
            const raw = data?.locations?.nodes;
            const apiList = raw?.length ? raw.map(normalizeLocationNode) : [];
            const hadApi = apiList.length > 0;
            this.locations = apiList;
            if (!data?.locations) {
              console.warn(
                "[store-selector] Storefront returned no locations data. Confirm the app token includes unauthenticated_read_product_pickup_locations, the API version matches your shop, and check GraphQL/HTTP messages logged above.",
              );
            } else if (!hadApi && !this.locations.length) {
              console.info(
                "[store-selector] No pickup locations from Storefront. Enable local pickup on at least one location (Shopify Admin → Settings → Locations) and confirm the Storefront token includes unauthenticated_read_product_pickup_locations.",
              );
            }
          } catch (e) {
            console.warn(
              "[store-selector] Failed to process locations response",
              e,
            );
            this.locations = [];
          }
          enrichLocationsWithGeocodedAddresses(this.locations).finally(() => {
            this.refreshDistanceOrdering();
          });
        };

        return storefrontFetch(locationsQuery(ns)).then((data) => {
          const raw = data?.locations?.nodes;
          if (raw?.length || data?.locations) {
            return finish(data);
          }
          return storefrontFetch(locationsQueryCoreOnly()).then(finish);
        });
      }

      loadGoogleMaps(cb) {
        ensureStoreSelectorGoogleMapsScript().then(() => {
          if (typeof cb !== "function") return;
          try {
            cb();
          } catch (e) {
            console.warn("[store-selector] Google Maps callback error", e);
          }
        });
      }

      syncSearchInputFromStorage() {
        let userLocation = null;
        try {
          userLocation = window.localStorage.getItem("user-location")
            ? JSON.parse(window.localStorage.getItem("user-location"))
            : null;
        } catch (e) {
          /* ignore */
        }
        if (this.input && userLocation)
          this.input.value = userLocation.name || "";
      }

      calculateLocationDistances() {
        const raw = window.localStorage.getItem("user-location");
        if (!raw) {
          updateStoreSelectorHeaderVisibility();
          return;
        }
        let userLocation;
        try {
          userLocation = JSON.parse(raw);
        } catch (e) {
          updateStoreSelectorHeaderVisibility();
          return;
        }
        const lat = parseFloat(userLocation.latitude);
        const lng = parseFloat(userLocation.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          updateStoreSelectorHeaderVisibility();
          return;
        }

        const locationDistances = this.locations.map((location) => {
          const la = location.address || {};
          const lat2 = parseFloat(la.latitude);
          const lng2 = parseFloat(la.longitude);
          const distance =
            Number.isNaN(lat2) || Number.isNaN(lng2)
              ? Infinity
              : haversineMeters(lat, lng, lat2, lng2);
          return { id: location.id, distance };
        });
        locationDistances.sort(
          (a, b) => parseFloat(a.distance) - parseFloat(b.distance),
        );
        window.localStorage.setItem(
          "location-distances",
          JSON.stringify(locationDistances),
        );
        updateStoreSelectorHeaderVisibility();
      }

      refreshDistanceOrdering() {
        try {
          this.calculateLocationDistances();
        } catch (e) {
          updateStoreSelectorHeaderVisibility();
        }
        this.buildLocationsCount();
        this.buildLocationsList();
      }

      buildLocationsCount() {
        let locationDistances = null;
        let userLocation = null;
        try {
          locationDistances = window.localStorage.getItem("location-distances")
            ? JSON.parse(window.localStorage.getItem("location-distances"))
            : null;
        } catch (e) {
          /* ignore */
        }
        try {
          userLocation = window.localStorage.getItem("user-location")
            ? JSON.parse(window.localStorage.getItem("user-location"))
            : null;
        } catch (e) {
          /* ignore */
        }

        const searchActive = (this._searchFilter || "").trim().length > 0;
        const usedProximityCount = !!(
          locationDistances?.length && this.locations?.length
        );
        let count = 0;
        if (usedProximityCount) {
          for (const ld of locationDistances) {
            const loc = this.locations.find((l) => l.id === ld.id);
            if (!loc) continue;
            if (!this.locationMatchesSearchFilter(loc)) continue;
            count += 1;
          }
        } else if (this.locations?.length) {
          count = this.locations.filter((l) =>
            this.locationMatchesSearchFilter(l),
          ).length;
        }

        if (this.locationsCount)
          this.locationsCount.textContent = String(count);

        if (this.locationsCountText) {
          if (searchActive) {
            this.locationsCountText.textContent = strCfg(
              "matchingSearchSuffix",
              "MATCHING STORES",
            );
          } else if (userLocation?.name?.trim()) {
            this.locationsCountText.textContent = `${strCfg("storesNearLabel", "NEAR")} ${userLocation.name.trim()}`;
          } else {
            this.locationsCountText.textContent = strCfg(
              "totalStoresSuffix",
              "TOTAL STORES",
            );
          }
        }

        /** Drawer lists all stores regardless of theme max distance (header-only gate). */
        if (this.locationsCountRow) this.locationsCountRow.hidden = false;
        if (this.emptyNearbyEl) this.emptyNearbyEl.hidden = true;
      }

      buildLocationsList() {
        if (this._autoSelectingNearestLocation) return Promise.resolve();
        let selectedLocation = null;
        let locationDistances = null;
        try {
          selectedLocation = window.localStorage.getItem("selected-location")
            ? JSON.parse(window.localStorage.getItem("selected-location"))
            : null;
        } catch (e) {
          /* ignore */
        }
        try {
          if (!window.localStorage.getItem("user-location")) {
            if (window.localStorage.getItem("location-distances"))
              window.localStorage.removeItem("location-distances");
          }
        } catch (e) {
          /* ignore */
        }
        try {
          locationDistances = window.localStorage.getItem("location-distances")
            ? JSON.parse(window.localStorage.getItem("location-distances"))
            : null;
        } catch (e) {
          /* ignore */
        }

        if (this._drawerListSnapshot) {
          return this._autoSelectNearestLocationIfNeeded().then((didAutoSelect) => {
            if (didAutoSelect) return;
            this._applyLocationsListDom(
              selectedLocation,
              locationDistances,
              this._drawerListSnapshot.productVariant,
              this._drawerListSnapshot.cartVariantInventoryLevels,
            );
          });
        }

        return getCart()
          .catch(() => ({ items: [] }))
          .then((cart) => {
            const variantIds = (cart.items || []).map((item) =>
              item.variant_id != null ? item.variant_id : item.id,
            );
            const gids = toVariantGids(variantIds);
            const productVariantPromise = window.location.pathname.includes(
              "/products/",
            )
              ? (() => {
                const inp =
                  document.querySelector(
                    'input.product-variant-id[name="id"]',
                  ) ||
                  document.querySelector(
                    'form[id^="main-product-form"] input[name="id"]',
                  );
                if (!inp?.value) return Promise.resolve(null);
                const one = toVariantGids([inp.value]);
                if (!one.length) return Promise.resolve(null);
                return storefrontFetch(variantsByIdsQuery(), { ids: one });
              })()
              : Promise.resolve(null);

            const cartLevelsPromise =
              gids.length > 0
                ? storefrontFetch(variantsByIdsQuery(), { ids: gids })
                : Promise.resolve(null);

            return Promise.all([productVariantPromise, cartLevelsPromise]).then(
              (results) => {
                const [productVariant, cartVariantInventoryLevels] = results;
                this._drawerListSnapshot = {
                  productVariant,
                  cartVariantInventoryLevels,
                };
                return this._autoSelectNearestLocationIfNeeded().then(
                  (didAutoSelect) => {
                    if (didAutoSelect) return;
                    this._applyLocationsListDom(
                      selectedLocation,
                      locationDistances,
                      productVariant,
                      cartVariantInventoryLevels,
                    );
                  },
                );
              },
            );
          });
      }

      addLocation(
        location,
        selectedLocation,
        productVariant,
        cartVariantInventoryLevels,
        distanceMeters,
      ) {
        const cfg = window.storeSelector || {};
        const inStockItems = [];
        const outOfStockItems = [];
        let productAvailable = false;
        let productTitle = "";
        const pvNode = productVariant?.nodes?.[0] || null;

        if (pvNode) {
          productTitle =
            pvNode.title?.includes("Default Title") && pvNode.product?.title
              ? pvNode.product.title
              : pvNode.title;
          const sa = pvNode.storeAvailability?.nodes;
          if (sa) {
            for (const node of sa) {
              if (node.location?.id === location.id && node.available) {
                productAvailable = true;
                break;
              }
            }
          }
        }

        if (cartVariantInventoryLevels?.nodes) {
          cartVariantInventoryLevels.nodes.forEach((variant) => {
            const nodes = variant.storeAvailability?.nodes;
            if ((!nodes || nodes.length === 0) && !variant.requiresShipping) {
              return;
            }

            let matchingLocation = null;

            if (nodes && nodes.length > 0) {
              for (const n of nodes) {
                if (n.location?.id === location.id) {
                  matchingLocation = n;
                  break;
                }
              }
            }

            if (matchingLocation) {
              if (matchingLocation.available) {
                inStockItems.push(matchingLocation);
              } else {
                outOfStockItems.push(matchingLocation);
              }
            }
          });
        }

        const locationEmail = metafieldByKey(location, "store_email");
        const locationInstagram = metafieldByKey(location, "store_instagram");
        const locationHours = metafieldByKey(location, "store_hours");
        const locationHoursRich = metafieldByKey(location, "location_hours");

        const addr = location.address || {};
        const addrLine = [
          addr.address1 ? `${addr.address1},` : "",
          addr.address2 ? `${addr.address2},` : "",
          addr.city ? `${addr.city},` : "",
          addr.province ? `${addr.province},` : "",
          addr.zip || "",
        ]
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();

        const distLabel = formatMilesFromMeters(distanceMeters);

        const isSel = selectedLocation?.id === location.id;
        const wrap = document.createElement("div");
        wrap.className = `store-selector__card${isSel ? " is-selected" : ""}`;
        wrap.setAttribute("data-store-selector-location", "");

        let html = "";
        const titleText = escapeHtml(storeDisplayLabel(location));
        const detailUrl = locationPageUrlFromLocation(location);
        html += detailUrl
          ? `<h3 class="store-selector__card-title"><a class="store-selector__card-title-link" href="${escapeHtml(detailUrl)}">${titleText}</a></h3>`
          : `<h3 class="store-selector__card-title">${titleText}</h3>`;
        if (distLabel) {
          const distText = strCfg(
            "distanceFromLocation",
            "{distance} away",
          ).replace(/\{distance\}/g, distLabel);
          html += `<p class="store-selector__card-distance">${escapeHtml(distText)}</p>`;
        }

        if (pvNode) {
          html += `<p class="store-selector__card-note store-selector__card-note--product-${productAvailable ? "available" : "unavailable"}"><span class="store-selector__card-note-mark">${productAvailable ? "✓" : "✗"}</span><span>${escapeHtml(productTitle || "")}  <span class="availability-status-${escapeHtml(productAvailable ? cfg.available || "" : cfg.unavailable || "")}">${escapeHtml(productAvailable ? cfg.available || "" : cfg.unavailable || "")}</span></span></p>`;
        }

        const contactBits = [];
        if (addrLine) {
          const mapsUrl = googleMapsSearchUrlFromAddress(addr);
          const safeLine = escapeHtml(addrLine);
          const addrVal = mapsUrl
            ? `<a class="store-selector__card-address-link" href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener noreferrer">${safeLine}</a>`
            : safeLine;
          contactBits.push(
            `<div class="store-selector__card-row"><span class="store-selector__card-ico">${STORE_LOCATOR_ICON_SVG}</span><span class="store-selector__card-val">${addrVal}</span></div>`,
          );
        }
        if (locationHoursRich?.value) {
          const hoursRichHtml = convertRichMetafieldValue(
            locationHoursRich.value,
          );
          if (hoursRichHtml) {
            contactBits.push(
              `<div class="store-selector__card-row store-selector__card-row--location-hours"><span class="store-selector__card-ico" aria-hidden="true"></span><div class="store-selector__card-val store-selector__card-val--richtext">${hoursRichHtml}</div></div>`,
            );
          }
        }
        if (addr.phone) {
          const tel = String(addr.phone).replace(/\s+/g, "");
          const phoneLabel = escapeHtml(
            formatPhoneNumber(String(addr.phone).replace("+1", "")) ||
            addr.phone,
          );
          contactBits.push(
            `<div class="store-selector__card-row"><span class="store-selector__card-ico"><i class="icon-phone" aria-hidden="true"></i></span><span class="store-selector__card-val"><a href="tel:${escapeHtml(tel)}">${phoneLabel}</a></span></div>`,
          );
        }
        if (locationEmail?.value) {
          const em = String(locationEmail.value).trim();
          const safeText = escapeHtml(em);
          const mailHref = escapeHtml(encodeURI(`mailto:${em}`));
          contactBits.push(
            `<div class="store-selector__card-row"><span class="store-selector__card-ico"><i class="icon-envelope" aria-hidden="true"></i></span><span class="store-selector__card-val"><a href="${mailHref}">${safeText}</a></span></div>`,
          );
        }
        if (locationInstagram?.value) {
          const ig = String(locationInstagram.value).replace(/^@/, "");
          const igSafe = escapeHtml(ig);
          contactBits.push(
            `<div class="store-selector__card-row"><span class="store-selector__card-ico"><i class="icon-instagram" aria-hidden="true"></i></span><span class="store-selector__card-val"><a href="https://www.instagram.com/${igSafe}" target="_blank" rel="noopener noreferrer">@${igSafe}</a></span></div>`,
          );
        }
        if (contactBits.length) {
          html += `<div class="store-selector__card-contact">${contactBits.join("")}</div>`;
        }

        if (locationHours?.value) {
          html += `<details class="store-selector__hours"><summary class="store-selector__hours-summary"><span class="store-selector__hours-summary-text">${escapeHtml(cfg.storeHours || "Hours")}</span><i class="icon-chevron-down store-selector__hours-chev" aria-hidden="true"></i></summary><div class="store-selector__hours-body">${convertRichMetafieldValue(locationHours.value)}</div></details>`;
        }

        const pickLabel = isSel
          ? cfg.selected || "Selected"
          : cfg.selectStore || cfg.select || "Select";

        const body = document.createElement("div");
        body.className = "store-selector__card-body";
        body.innerHTML = html;

        const pickBtn = document.createElement("button");
        pickBtn.type = "button";
        pickBtn.className = `store-selector__pick${isSel ? " store-selector__pick--selected" : ""}`;
        pickBtn.setAttribute("data-store-selector-store-select", "");
        pickBtn.setAttribute("data-location-id", String(location.id));
        pickBtn.textContent = pickLabel;

        wrap.appendChild(body);
        wrap.appendChild(pickBtn);
        this.locationsList.appendChild(wrap);
      }

      getCurrentLocation(silent) {
        const isSilent = !!silent;
        if (!navigator.geolocation) {
          if (!isSilent)
            window.alert(
              strCfg("geolocationNotSupported", "Geolocation not supported"),
            );
          return;
        }
        if (this.useCurrentLocationText && !isSilent) {
          this.useCurrentLocationText.innerText = strCfg("loading", "…");
        }
        const geoOpts = {
          enableHighAccuracy: false,
          timeout: 18000,
          maximumAge: 60000,
        };
        navigator.geolocation.getCurrentPosition(
          (pos) => this.showPosition(pos),
          (err) => this.handleGeolocationError(err, isSilent),
          geoOpts,
        );
      }

      showPosition(position) {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const input = document.querySelector("[data-store-selector-input]");

        const save = (name) => {
          window.localStorage.setItem(
            "user-location",
            JSON.stringify({
              latitude: lat,
              longitude: lng,
              name: name || strCfg("currentLocationLabel", "Current location"),
            }),
          );
          if (input) input.value = name || strCfg("currentLocationLabel", "");
          document.dispatchEvent(new Event("calculateDistances"));
        };

        if (window.google?.maps) {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results?.[0]) {
              save(results[0].formatted_address);
            } else {
              save(strCfg("currentLocationLabel", ""));
            }
          });
          return;
        }

        if (!window.storeSelector?.googleMapsKey) {
          save(strCfg("currentLocationLabel", ""));
          return;
        }

        this.loadGoogleMaps(() => {
          if (window.google?.maps) {
            this.showPosition(position);
          } else {
            save(strCfg("currentLocationLabel", ""));
          }
        });
      }

      handleGeolocationError(error, silent) {
        if (error?.code === error.PERMISSION_DENIED) {
          try {
            sessionStorage.setItem("store_selector_geo_denied", "1");
          } catch (e) {
            /* ignore */
          }
        }
        if (silent) {
          updateStoreSelectorHeaderVisibility();
          return;
        }
        const useCurrentLocationText = document.querySelector(
          "[data-store-selector-use-current-location-text]",
        );
        let msg = strCfg("geolocationUnknownError", "");
        if (error?.code === error.PERMISSION_DENIED)
          msg = strCfg("geolocationPermissionDenied", msg);
        else if (error?.code === error.POSITION_UNAVAILABLE)
          msg = strCfg("geolocationPositionUnavailable", msg);
        else if (error?.code === error.TIMEOUT)
          msg = strCfg("geolocationTimeout", msg);
        if (useCurrentLocationText && msg)
          useCurrentLocationText.innerText = msg;
        updateStoreSelectorHeaderVisibility();
      }

      setCartAttributeWithFormData(formData) {
        return fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          body: formData,
        }).then((response) => {
          if (!response.ok) {
            throw new Error(
              `Cart update failed with status ${response.status}`,
            );
          }
          return response.json();
        });
      }

      async selectLocation(location) {
        window.localStorage.setItem(
          "selected-location",
          JSON.stringify(location),
        );
        try {
          await syncSelectedLocationToCart(location);
        } catch {
          /* errors logged inside syncSelectedLocationToCart */
        }
        try {
          const formData = new FormData();
          const addr = location.address || {};
          const addrLine = [
            addr.address1 ? `${addr.address1},` : "",
            addr.address2 ? `${addr.address2},` : "",
            addr.city ? `${addr.city},` : "",
            addr.province ? `${addr.province},` : "",
            addr.zip || "",
          ]
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();
          const safeLine = escapeHtml(addrLine);
          formData.append("attributes[bm_store_location_address]", safeLine);
          formData.append('attributes[bm_store_location_zip]', location.address.zip);
          this.setCartAttributeWithFormData(formData);
        } catch {
          /* errors logged inside syncSelectedLocationToCart */
        }
        document.dispatchEvent(
          new CustomEvent("store-selector:selected-location", {
            bubbles: true,
            detail: { location },
          }),
        );
        document.querySelectorAll("store-selector-toggle").forEach((toggle) => {
          if (typeof toggle.updateButtonText === "function")
            toggle.updateButtonText();
        });
        this.buildLocationsList();
        this.close();
        if (typeof window.storeSelectorOnLocationSelected === "function") {
          window.storeSelectorOnLocationSelected(location);
        }
      }

      close() {
        document.documentElement.classList.remove("store-selector-drawer-open");
        document.body.classList.remove("overflow-hidden");
        this.classList.remove("active");
        this.setAttribute("aria-hidden", "true");
        const aside = document.getElementById("store-locator");
        if (aside?.classList.contains("toggle")) {
          if (typeof hidePanels === "function") {
            hidePanels();
          } else {
            aside.classList.remove("toggle");
            aside.setAttribute("aria-hidden", "true");
            if (!document.querySelector(".m6pn.toggle")) {
              document.documentElement.classList.remove(
                "m6pn-open",
                "m6cp-open",
                "f8fl-open",
              );
            }
          }
        }
      }

      requestPassiveGeoForDistancesIfNeeded() {
        beginPassiveGeoIfNoUserLocation();
      }

      open() {
        void syncPreferredPickupToCartFromLocalStorage();
        this.setAttribute("aria-hidden", "false");
        if (typeof openPanel === "function") {
          openPanel("store-locator");
        } else {
          const aside = document.getElementById("store-locator");
          if (aside) {
            document.querySelectorAll(".m6pn").forEach((el) => {
              el.classList.remove("toggle");
              el.setAttribute("aria-hidden", "true");
            });
            aside.classList.add("toggle");
            aside.setAttribute("aria-hidden", "false");
            document.documentElement.classList.add("has-panels", "m6pn-open");
          }
        }

        this.requestPassiveGeoForDistancesIfNeeded();
        if (!this.classList.contains("loaded")) {
          this.syncSearchInputFromStorage();
          this.buildLocationsCount();
          this.buildLocationsList();
          this.classList.add("loaded");
        } else {
          this.syncSearchInputFromStorage();
        }
        this.scheduleSearchUiInit();
        document.documentElement.classList.add("store-selector-drawer-open");
      }
    }

    window.customElements.define("store-selector", StoreSelector);
  }

  if (!window.customElements.get("store-selector-toggle")) {
    class StoreSelectorToggle extends HTMLElement {
      constructor() {
        super();
        this.button = null;
        this.selectedLocationText = null;
        this._open = null;
      }

      connectedCallback() {
        this.button = this.querySelector("button");
        this.selectedLocationText = this.querySelector(
          "[data-selected-location]",
        );
        this._open = () =>
          document.dispatchEvent(new Event("openStoreSelector"));
        if (this.button) this.button.addEventListener("click", this._open);
        this.updateButtonText();
      }

      disconnectedCallback() {
        if (this.button && this._open)
          this.button.removeEventListener("click", this._open);
      }

      updateButtonText() {
        let selectedLocation = null;
        try {
          selectedLocation = window.localStorage.getItem("selected-location")
            ? JSON.parse(window.localStorage.getItem("selected-location"))
            : null;
        } catch (e) {
          /* ignore */
        }
        const cta = this.querySelector("[data-store-selector-cta]");
        const ctaText =
          window.storeSelector?.selectYourStore &&
            String(window.storeSelector.selectYourStore).trim()
            ? window.storeSelector.selectYourStore
            : "Select your store";
        if (cta) {
          if (selectedLocation) {
            cta.classList.add("hidden");
          } else {
            cta.classList.remove("hidden");
            cta.textContent = ctaText;
          }
        }
        if (!this.selectedLocationText) return;
        if (!selectedLocation) {
          this.selectedLocationText.textContent = "";
          return;
        }
        const a = selectedLocation.address || {};
        const line = `${a.city || ""}${a.provinceCode ? `, ${a.provinceCode}` : a.province ? `, ${a.province}` : ""}`;
        this.selectedLocationText.textContent =
          line.trim() || selectedLocation.name || "";
      }
    }

    window.customElements.define("store-selector-toggle", StoreSelectorToggle);
  }
})();